import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createOrder, fetchAllOrders,updateOrder } from './orderAPI';
import { API_URL } from '../../app/apiConfig';

const initialState = {
  orders: [],
  status: 'idle',
  currentOrder: null,
  pendingOrder: null, // Order data waiting for receipt upload
  totalOrders: 0,
  error: null
};
//we may need more info of current order

export const createOrderAsync = createAsyncThunk(
  'order/createOrder',
  async (order) => {
    const response = await createOrder(order);
    // The value we return becomes the `fulfilled` action payload
    return response.data;
  }
);
export const updateOrderAsync = createAsyncThunk(
  'order/updateOrder',
  async (order) => {
    const response = await updateOrder(order);
    // The value we return becomes the `fulfilled` action payload
    return response.data;
  }
);

export const fetchAllOrdersAsync = createAsyncThunk(
  'order/fetchAllOrders',
  async ({sort, pagination}) => {
    const response = await fetchAllOrders(sort,pagination);
    // The value we return becomes the `fulfilled` action payload
    return response.data;
  }
);

export const createOrderWithReceiptAsync = createAsyncThunk(
  'order/createOrderWithReceipt',
  async (formData) => {
    
    // Get the auth token from localStorage
    const token = localStorage.getItem('token');
    
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/orders/create-with-receipt`, {
      method: 'POST',
      headers: headers,
      body: formData,
      credentials: 'include',
    });

    const raw = await response.text();
    let data = {};
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch (e) {
      data = {};
    }
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create order with receipt');
    }

    const orderId = data?.id || data?._id || data?.order?.id || data?.order?._id;
    if (!orderId) {
      throw new Error('Order created but no order ID returned');
    }

    // Keep payload tiny to avoid heavy dev middleware freezes.
    return { id: String(orderId) };
  }
);

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    resetOrder: (state) => {
      state.currentOrder = null;
    },
    setPendingOrder: (state, action) => {
      state.pendingOrder = action.payload;
    },
    clearPendingOrder: (state) => {
      state.pendingOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrderAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createOrderAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.orders.push(action.payload);
        state.currentOrder = action.payload;
        state.error = null;
      })
      .addCase(createOrderAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload?.error || action.error?.message || 'Failed to create order';
      })
      .addCase(fetchAllOrdersAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllOrdersAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.orders = Array.isArray(action.payload?.orders) ? action.payload.orders : [];
        state.totalOrders = action.payload?.totalOrders || 0;
      })
      .addCase(fetchAllOrdersAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.orders = [];
        state.error = action.payload?.error || action.error?.message;
      })
      .addCase(updateOrderAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateOrderAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        const index =  state.orders.findIndex(order=>order.id===action.payload.id)
        state.orders[index] = action.payload;
      })
      .addCase(createOrderWithReceiptAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createOrderWithReceiptAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.currentOrder = action.payload;
        state.pendingOrder = null;
        state.error = null;
      })
      .addCase(createOrderWithReceiptAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload?.error || action.error?.message || 'Failed to create order with receipt';
      })
  },
});

export const { resetOrder, setPendingOrder, clearPendingOrder } = orderSlice.actions;

export const selectCurrentOrder = (state) => state.order.currentOrder;
export const selectPendingOrder = (state) => state.order.pendingOrder;
export const selectOrders = (state) => state.order.orders;
export const selectTotalOrders = (state) => state.order.totalOrders;
export const selectStatus = (state) => state.order.status;
export const selectError = (state) => state.order.error;

export default orderSlice.reducer;
